// #region imports

// --- Angular
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

// --- RxJs
import { Observable, of, zip } from 'rxjs';
import { catchError } from 'rxjs/operators';

// --- 3rd Party
import { retryBackoff } from 'backoff-rxjs';
import { TranslateService } from '@ngx-translate/core';

// --- HS Code
import { QuestionsBaseService } from './questions-base.service';
import { environment } from '../../../../environments/environment';
import { ApplicationError, ErrorMessage, QuestionModel } from '../../../models';
import { AppService } from '../app.service';

// #endregion

@Injectable({
  providedIn: 'root',
})
export class QuestionsService extends QuestionsBaseService {
  private _questions: QuestionModel[] = new Array<QuestionModel>();
  private _surveyId: string = '';

  constructor(
    private http: HttpClient,
    private appService: AppService,
    private translate: TranslateService
  ) {
    super();
  }

  loadQuestions(id: string, isParticipant: boolean = true, force?: boolean): void {
    if (
      !force &&
      this._questions &&
      this._questions.length > 0 &&
      this._surveyId === id
    ) {
      this.initialiseQuestions(this._questions);
      return;
    }

    const questionsUrl =
      environment.apiConfig.surveyApiUrl + environment.apiConfig.questionsApi;
    const evQuestionsUrl =
      environment.apiConfig.surveyApiUrl +
      environment.apiConfig.questionsApi +
      `/${id}`;

    if (isParticipant) {
      zip(
        this.http.get<QuestionModel[]>(questionsUrl),
        this.http.get<QuestionModel[]>(evQuestionsUrl)
      ).pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._modelError.bind(this))
      )
      .subscribe((result) => {
        if (result) {
          this._questions = new Array<QuestionModel>().concat(result[0], result[1]);
          this._surveyId = id;
          this.initialiseQuestions(this._questions);
        }
      });
    } else {
      this.http.get<QuestionModel[]>(questionsUrl).pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._modelError.bind(this))
      ).subscribe((result) => {
        if (result) {
          this._questions = result;
          this._surveyId = id;
          this.initialiseQuestions(this._questions);
        }
      });
    }
  }

  private _modelError(err: any): Observable<QuestionModel[] | null> {
    this._handleError(err);
    return of(null);
  }

  private _handleError(err: any) {
    this.appService.setLoading(false);

    const error: ApplicationError = ApplicationError.create(
      ErrorMessage.genericErrorCode,
      'QuestionsService',
      true,
      err
    );

    this.appService.setLastError(error);

    this._questions$.next(null);
  }
}
